import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { FileText, Frame, Search as SearchIcon, Sparkles } from 'lucide-react';
import { Kbd, KbdGroup } from '../shared/Kbd.jsx';
import { Tabs, TabsList, TabsTab } from '../shared/Tabs.jsx';
import { moveHoverGlide } from './glide.jsx';
import { search, wantsMeaning, normalizeQuery } from './tree.js';
import { previewOf } from './preview-api.js';

/**
 * The trigger and the menu are the same row. Opening widens that row to
 * 640px, then grows its height. Closing does the reverse: height first,
 * then width. Width and height never run together, so the card does not
 * scale on a diagonal.
 */

const CLOSED_RADIUS = 8;
const OPEN_RADIUS = 12;
const OPEN_WIDTH = 640;
// Search by meaning (config `ask`, dev server only): asked after a pause in
// typing, for phrases that match no component name. Answers are cached for
// the session; while a new one loads the previous one stays, dimmed.
const PAUSE_MS = 400;
const answers = new Map();
const FIELD = 48;
const TABS = [
  { id: 'all', label: 'All' },
  { id: 'pages', label: 'Pages' },
  { id: 'stories', label: 'Canvases' },
];

const SPRING = { type: 'spring', stiffness: 450, damping: 25, mass: 0.1 };
const HEIGHT_SPRING = { type: 'spring', stiffness: 320, damping: 28, mass: 0.12 };
const REDUCED = { duration: 0 };
/* Start the next phase while this much of the current one is still left. */
const PHASE_LEAD = 0.06;

function useNarrow() {
  const query = '(max-width: 900px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function parts(entry) {
  const segs = entry.title.split('/').map((s) => s.trim()).filter(Boolean);
  return {
    section: segs.length > 1 ? segs[0] : 'Library',
    label: segs.at(-1) || entry.name,
    description: entry.type === 'story' ? entry.name : 'Page',
  };
}

/** Grouped rows for the menu. An empty query lists the library; a query ranks it. */
function commandGroups(index, query, tab) {
  const allows = (entry) => tab === 'pages' ? entry.type === 'docs' : tab === 'stories' ? entry.type === 'story' : true;
  const ranked = query.trim() ? search(index, query) : index.order.map((id) => index.entries[id]);
  const entries = ranked.filter(allows).slice(0, 40);
  const groups = [];
  const bySection = new Map();
  for (const entry of entries) {
    const row = { ...parts(entry), id: entry.id, type: entry.type };
    let group = bySection.get(row.section);
    if (!group) {
      group = { id: row.section, label: row.section, items: [] };
      bySection.set(row.section, group);
      groups.push(group);
    }
    group.items.push(row);
  }
  if (!query.trim()) {
    for (const group of groups) {
      group.items.sort((a, b) => Number(a.type !== 'docs') - Number(b.type !== 'docs'));
    }
  }
  let indexInList = 0;
  for (const group of groups) {
    for (const item of group.items) item.index = indexInList++;
  }
  return groups;
}

function emptyCopy(query, tab) {
  if (!query.trim()) return tab === 'stories' ? 'No canvases yet.' : 'Nothing to show.';
  const kind = tab === 'pages' ? 'page' : tab === 'stories' ? 'canvas' : 'component or canvas';
  return `No ${kind} matches “${query.trim()}”.`;
}

export function Search({ index, ask = false, globals, open, onOpen, onClose, onSelect }) {
  const narrow = useNarrow();
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const scrollRef = useRef(null);
  const pointerRef = useRef(null);
  const hoverFrameRef = useRef(0);
  const wasOpen = useRef(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');
  const [active, setActive] = useState(0);
  // Results rise in when the filter changes, not when the menu opens.
  const [tabSwitched, setTabSwitched] = useState(false);
  const switchTab = (next) => {
    setTabSwitched(true);
    setActive(0);
    setTab(next);
  };
  const [shown, setShown] = useState(false);
  const [resting, setResting] = useState(false);
  const groups = useMemo(() => commandGroups(index, query, tab), [index, query, tab]);
  const local = useMemo(() => search(index, query), [index, query]);
  const asking = ask && tab !== 'pages' && wantsMeaning(query, local);
  // A hidden preview reads each answer's controls, so the answer can show the
  // state the request describes (src/node/ask-args.js). It loads on first open.
  const frameRef = useRef(null);
  const [frameOn, setFrameOn] = useState(false);
  useEffect(() => {
    if (open && ask) setFrameOn(true);
  }, [open, ask]);
  const contextRef = useRef(null);
  contextRef.current = { index, globals, frame: frameRef };
  const meaning = useMeaning(asking, query, contextRef);
  const named = groups.reduce((acc, group) => acc.concat(group.items), []);
  const meaningRows = (meaning.data?.results ?? []).map((r, j) => {
    const segs = r.title.split('/').map((x) => x.trim());
    return { id: r.storyId, label: segs.at(-1), description: r.storyName, set: setText(r), state: r.args || r.globals ? { args: r.args, globals: r.globals } : null, section: segs.slice(0, -1).join(' / '), index: named.length + j, meaning: true };
  });
  const flat = named.concat(meaningRows);
  const menuRef = useRef(null);
  const settled = useRef(false);
  const top = useMotionValue(0);
  const left = useMotionValue(0);
  const width = useMotionValue(0);
  const height = useMotionValue(0);
  const radiusMv = useMotionValue(CLOSED_RADIUS);
  const escOpacity = useMotionValue(0);
  const cmdOpacity = useTransform(escOpacity, (value) => 1 - value);
  const rowHeightRef = useRef(36);
  // 0 when the row matches the trigger, 1 when it sits in the 48px field.
  // Kept off the card height so the close can narrow before the height
  // spring has crawled to a stop, without the padding riding a second curve.
  const fieldT = useMotionValue(0);
  const rowShift = useTransform(fieldT, (t) => {
    const span = FIELD - rowHeightRef.current;
    if (span <= 0) return 0;
    const blend = Math.min(Math.max(t, 0), 1);
    return blend * (span / 2);
  });
  // Cap inset in the 36px row is 8px. It grows with the field, up to 14px at 48px.
  const kbdInset = useTransform(rowShift, (shift) => (36 - 20) / 2 + shift);
  // Trigger has no extra left padding. The open field has 4px.
  const iconInset = useTransform(fieldT, (t) => Math.min(Math.max(t, 0), 1) * 4);
  // The card lifts by the same amount the row drops, both as transforms, so
  // the glyphs stay put. `top` stays on the trigger; rounding it against a
  // translate made the placeholder shiver.
  const cardLift = useTransform(rowShift, (shift) => -shift);
  // Height the card is opening to. The body fades across that span, so the
  // content appears with the expansion instead of being uncovered already lit.
  const openHeightRef = useRef(0);
  const bodyOpacity = useTransform(height, (card) => {
    if (settled.current) return 1;
    const from = Math.max(FIELD, rowHeightRef.current);
    const span = openHeightRef.current - from;
    if (span <= 1) return 0;
    return Math.min(1, Math.max(0, (card - from) / span));
  });

  useEffect(() => setActive(0), [query]);

  // Width, then height. Closing runs the same steps backwards.
  useEffect(() => {
    const token = {};
    let current = token;
    const controls = [];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const play = (value, to, spring = SPRING) => {
      const run = animate(value, to, reduced ? REDUCED : spring);
      controls.push(run);
      return { run, finished: run.finished.catch(() => 'cancelled'), value, to };
    };
    const stopped = (results) => current !== token || results.includes('cancelled');
    // Resolves slightly before the animation settles, without stopping it.
    const nearly = (step) => nearlyThere(step.run, step.value, step.to, PHASE_LEAD);

    (async () => {
      if (open) {
        settled.current = false;
        setResting(false);
        const trigger = triggerRef.current?.getBoundingClientRect();
        if (!trigger) return;
        const to = placeMenu(trigger, narrow);
        if (!shown) {
          top.set(trigger.top);
          left.set(trigger.left);
          width.set(trigger.width);
          height.set(trigger.height);
          rowHeightRef.current = trigger.height;
          fieldT.set(0);
          radiusMv.set(CLOSED_RADIUS);
          escOpacity.set(0);
          setResting(false);
          setShown(true);
          await frame();
          if (!menuRef.current) await frame();
          if (current !== token) return;
          inputRef.current?.focus();
        }
        const widthStep = play(width, to.width);
        const leftStep = play(left, to.left);
        const topStep = play(top, to.top);
        const radiusStep = play(radiusMv, OPEN_RADIUS);
        const escStep = play(escOpacity, 1);
        play(height, Math.max(FIELD, trigger.height));
        const fieldBlend = play(fieldT, 1);
        const gate = await nearly(widthStep);
        if (gate === 'cancelled' || current !== token) return;
        const full = menuHeight(menuRef.current, to.top);
        openHeightRef.current = full;
        const heightStep = play(height, full, HEIGHT_SPRING);
        const grown = await Promise.all([
          widthStep.finished,
          leftStep.finished,
          topStep.finished,
          radiusStep.finished,
          escStep.finished,
          fieldBlend.finished,
          heightStep.finished,
        ]);
        if (stopped(grown)) return;
        settled.current = true;
        wasOpen.current = true;
        return;
      }
      if (!menuRef.current) return;
      openHeightRef.current = height.get();
      settled.current = false;
      const row = triggerRef.current?.getBoundingClientRect().height ?? 36;
      rowHeightRef.current = row;
      const heightStep = play(height, row, HEIGHT_SPRING);
      const shrunk = await nearly(heightStep);
      if (shrunk === 'cancelled' || current !== token) return;
      const back = triggerRef.current?.getBoundingClientRect();
      if (!back) return;
      // Narrow while the height spring is still finishing. The field padding
      // comes back with this same spring, not with the height.
      setQuery('');
      setResting(true);
      const narrowed = await Promise.all([
        heightStep.finished,
        play(left, back.left).finished,
        play(top, back.top).finished,
        play(width, back.width).finished,
        play(fieldT, 0).finished,
        play(radiusMv, CLOSED_RADIUS).finished,
        play(escOpacity, 0).finished,
      ]);
      if (stopped(narrowed)) return;
      setShown(false);
      setQuery('');
      setTab('all');
      setTabSwitched(false);
      if (wasOpen.current) triggerRef.current?.focus();
      wasOpen.current = false;
    })();

    return () => {
      current = null;
      controls.forEach((run) => run.stop());
    };
  }, [open]);

  // Once the card has settled, keep it on the trigger while the rail slides.
  useEffect(() => {
    if (!open) return;
    let frameId = 0;
    const tick = () => {
      if (settled.current && triggerRef.current) {
        const to = placeMenu(triggerRef.current.getBoundingClientRect(), narrow);
        if (Math.round(top.get()) !== to.top) top.set(to.top);
        if (Math.round(left.get()) !== to.left) left.set(to.left);
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [open, narrow]);

  useEffect(() => {
    listRef.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active, groups]);

  // One highlight for the hovered or arrowed row. It fades in on the first
  // row, then springs between rows. Measured from the list, because a section
  // is positioned and would otherwise report a row's box from its own top.
  const glowRef = useRef(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    const glow = glowRef.current;
    if (!list || !glow) return undefined;
    const place = () => moveHoverGlide(glow, list, list.querySelector('.shell-search-item.is-active'));
    place();
    const observer = new ResizeObserver(place);
    observer.observe(list);
    return () => observer.disconnect();
  }, [active, groups, shown]);

  useEffect(() => {
    if (!settled.current || !menuRef.current) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    animate(height, menuHeight(menuRef.current, top.get()), reduced ? REDUCED : HEIGHT_SPRING);
  }, [groups, meaning.status, meaningRows.length, active]);

  const onKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!flat.length) return;
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!flat.length) return;
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && flat[active]) {
      if (e.target instanceof Element && e.target.closest('.edl-tabs')) return;
      e.preventDefault();
      onSelect(flat[active].id, flat[active].state);
    } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !query.trim()) {
      // The field is focused, so the tablist never sees these arrows. A tab
      // that does have focus keeps the component's own arrow handling.
      if (e.target instanceof Element && e.target.closest('.edl-tabs')) return;
      e.preventDefault();
      const at = TABS.findIndex((item) => item.id === tab);
      const step = e.key === 'ArrowRight' ? 1 : -1;
      switchTab(TABS[(at + step + TABS.length) % TABS.length].id);
    }
  };

  const holdFocus = (e) => {
    if (e.target instanceof HTMLElement && e.target.closest('input')) return;
    e.preventDefault();
  };

  const rememberPointer = (event) => {
    if (event.pointerType === 'touch') return;
    pointerRef.current = { x: event.clientX, y: event.clientY };
  };

  const followPointer = () => {
    hoverFrameRef.current = 0;
    const point = pointerRef.current;
    const scroller = scrollRef.current;
    if (!point || !scroller) return;
    const row = document.elementFromPoint(point.x, point.y)?.closest?.('.shell-search-item');
    if (!row || !scroller.contains(row)) return;
    const next = Number(row.dataset.searchIndex);
    if (!Number.isInteger(next)) return;
    setActive((current) => (current === next ? current : next));
  };

  const followScroll = () => {
    if (!pointerRef.current || hoverFrameRef.current) return;
    hoverFrameRef.current = requestAnimationFrame(followPointer);
  };

  useEffect(() => () => cancelAnimationFrame(hoverFrameRef.current), []);

  return (
    <div className="shell-search">
      <button
        ref={triggerRef}
        type="button"
        className="shell-search-trigger"
        style={{ borderRadius: CLOSED_RADIUS, visibility: shown ? 'hidden' : 'visible' }}
        aria-expanded={open || shown}
        aria-haspopup="dialog"
        aria-controls={shown ? 'edl-search' : undefined}
        aria-hidden={shown || undefined}
        tabIndex={shown ? -1 : undefined}
        onClick={onOpen}
      >
        <span className="shell-search-trigger__clip">
          <FieldIcon />
          <span className="shell-search__center">Search components</span>
          <span className="shell-search__kbd">
            <Shortcut />
          </span>
        </span>
      </button>

      {shown && (
        <motion.div
          className="shell-search-scrim"
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: open ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        />
      )}
      {shown && (
        <motion.div
          ref={menuRef}
          id="edl-search"
          className="shell-search-menu"
          data-collapsed={resting || undefined}
          style={{ position: 'fixed', top, y: cardLift, left, width, height, borderRadius: radiusMv }}
          role="dialog"
          aria-modal="true"
          aria-label="Search"
          onKeyDown={onKey}
          onMouseDown={holdFocus}
        >
          <div className="shell-search-menu__clip">
            <motion.div className="shell-search-menu__field" style={{ paddingLeft: iconInset, paddingRight: kbdInset }}>
              <motion.div className="shell-search-menu__row" style={{ y: rowShift }}>
              <FieldIcon />
              <span className="shell-search__center">
                <input
                  ref={inputRef}
                  className="shell-search__input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search components"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  role="combobox"
                  aria-expanded={flat.length > 0}
                  aria-controls="edl-search-results"
                  aria-activedescendant={flat[active] ? `sr-${flat[active].meaning ? 'm-' : ''}${flat[active].id}` : undefined}
                  aria-label="Search components"
                />
              </span>
              <span className="shell-search__kbd">
                <KbdFace opacity={cmdOpacity}>
                  <Shortcut />
                </KbdFace>
                <KbdFace opacity={escOpacity}>
                  <Kbd>esc</Kbd>
                </KbdFace>
              </span>
              </motion.div>
            </motion.div>
            <motion.div className="shell-search-menu__body" style={{ opacity: bodyOpacity }}>
              <Tabs className="shell-search-tabs" size="compact" value={tab} onValueChange={switchTab}>
                <TabsList aria-label="Filter">
                  {TABS.map((item) => (
                    <TabsTab key={item.id} value={item.id}>{item.label}</TabsTab>
                  ))}
                </TabsList>
              </Tabs>
              <div
                ref={scrollRef}
                className={`shell-search-menu__list${asking ? ' has-meaning' : ''}`}
                id="edl-search-results"
                role="listbox"
                onPointerEnter={rememberPointer}
                onPointerMove={rememberPointer}
                onPointerLeave={() => {
                  pointerRef.current = null;
                  cancelAnimationFrame(hoverFrameRef.current);
                  hoverFrameRef.current = 0;
                }}
                onPointerCancel={() => {
                  pointerRef.current = null;
                }}
                onScroll={followScroll}
              >
                <div key={tab} ref={listRef} className="shell-search-results" data-switched={tabSwitched ? '' : undefined}>
                  <div ref={glowRef} className="edl-hover-glide" aria-hidden="true" />
                  {groups.map((group) => (
                    <div key={group.id} role="group" aria-label={group.label}>
                      <div className="shell-search-group">{group.label}</div>
                      {group.items.map((item) => {
                        const Icon = item.type === 'story' ? Frame : FileText;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            id={`sr-${item.id}`}
                            role="option"
                            data-search-index={item.index}
                            aria-selected={item.index === active}
                            className={`shell-search-item${item.index === active ? ' is-active' : ''}`}
                            onMouseEnter={() => setActive(item.index)}
                            onClick={() => onSelect(item.id)}
                          >
                            <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
                            <span className="shell-search-item__label">{item.label}</span>
                            <span className="shell-search-item__desc">{item.description}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                  {asking && (
                    <MeaningGroup
                      rows={meaningRows}
                      status={meaning.status}
                      none={meaning.data?.none}
                      error={meaning.error}
                      active={active}
                      setActive={setActive}
                      globals={globals}
                      onSelect={onSelect}
                    />
                  )}
                  {flat.length === 0 && !asking && <p className="shell-search-none">{emptyCopy(query, tab)}</p>}
                </div>
              </div>
              <div className="shell-search-footer">
                <Hint label="Select">
                  <KbdGroup>
                    <Kbd>↑</Kbd>
                    <Kbd>↓</Kbd>
                  </KbdGroup>
                </Hint>
                {!query.trim() && (
                  <Hint label="Tabs">
                    <KbdGroup>
                      <Kbd>←</Kbd>
                      <Kbd>→</Kbd>
                    </KbdGroup>
                  </Hint>
                )}
                {flat[active] && (
                  <Hint className="shell-search-hint--go" label={`Go to ${flat[active].label}`}>
                    <Kbd>↵</Kbd>
                  </Hint>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
      {frameOn && <iframe ref={frameRef} className="shell-search-frame" src="/preview.html?idle" title="" aria-hidden="true" tabIndex={-1} />}
    </div>
  );
}

function Shortcut() {
  return (
    <KbdGroup>
      <Kbd>⌘</Kbd>
      <Kbd>K</Kbd>
    </KbdGroup>
  );
}

function KbdFace({ opacity, children }) {
  return (
    <motion.span className="shell-search__kbd-face" style={{ opacity }}>
      {children}
    </motion.span>
  );
}

function FieldIcon() {
  return (
    <span className="shell-search__icon">
      <SearchIcon size={16} strokeWidth={1.5} aria-hidden="true" />
    </span>
  );
}

function Hint({ label, className, children }) {
  return (
    <span className={['shell-search-hint', className].filter(Boolean).join(' ')}>
      <span className="shell-search-hint__label">{label}</span>
      {children}
    </span>
  );
}

/** Where the open card sits. Narrow fills the screen; otherwise it is 640px, centred on the trigger. */
function placeMenu(rect, narrow) {
  if (narrow) {
    const width = Math.round(window.innerWidth - 24);
    return { top: Math.round(rect.top), left: 12, width };
  }
  const width = Math.min(OPEN_WIDTH, window.innerWidth - 32);
  const ideal = rect.left + rect.width / 2 - width / 2;
  const left = Math.max(16, Math.min(ideal, window.innerWidth - width - 16));
  return { top: Math.round(rect.top), left: Math.round(left), width: Math.round(width) };
}

function frame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

/** Resolves when `lead` of the distance is left. The animation keeps running. */
function nearlyThere(run, value, to, lead) {
  const span = Math.abs(to - value.get());
  if (span < 1) return run.finished.catch(() => 'cancelled');
  return new Promise((resolve) => {
    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      unsub();
      resolve(result);
    };
    const unsub = value.on('change', (next) => {
      if (Math.abs(to - next) / span <= lead) finish(undefined);
    });
    run.finished.then(() => finish(undefined)).catch(() => finish('cancelled'));
  });
}

/** Natural card height, capped so the list scrolls instead of leaving the screen. */
function menuHeight(menu, top) {
  const cap = menu?.querySelector('.shell-search-menu__list.has-meaning') ? 600 : 440;
  const max = Math.min(cap, window.innerHeight - top - 16);
  if (!menu) return max;
  const field = menu.querySelector('.shell-search-menu__field')?.offsetHeight ?? 0;
  const body = menu.querySelector('.shell-search-menu__body')?.offsetHeight ?? 0;
  return Math.min(Math.max(field + body, field), max);
}

/** Fetches answers by meaning after a pause; drops answers for a query that moved on. */
function useMeaning(asking, query, contextRef) {
  const [state, setState] = useState({ status: 'idle' });
  useEffect(() => {
    if (!asking) return setState({ status: 'idle' });
    const q = normalizeQuery(query);
    if (answers.has(q)) return setState({ status: 'done', data: answers.get(q) });
    // Keep the previous answers on screen, dimmed, until the new ones land.
    setState((s) => (s.data ? { ...s, status: 'stale' } : { status: 'waiting' }));
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/__edl/ask', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: q }),
          signal: controller.signal,
        });
        const found = await res.json();
        if (!res.ok) throw new Error(found.error ?? `HTTP ${res.status}`);
        // Each answer in the state the request describes; an answer whose
        // state cannot be read stays as search found it. "Nothing fits" gets
        // it too: a request with details ("with a lock icon") often scores
        // under noneBelow while its closest answer is right.
        const results = await Promise.all(found.results.map((r) => withState(r, q, contextRef.current, controller.signal).catch(() => r)));
        const data = { ...found, results };
        answers.set(q, data);
        setState({ status: 'done', data });
      } catch (error) {
        if (error.name !== 'AbortError') setState({ status: 'error', error: error.message });
      }
    }, PAUSE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [asking, query]);
  return state;
}

/**
 * Describe a state on an answer ("a button to add a new client" → plus icon,
 * "Add new client"). Measured with tools/ask-eval/search-args-eval.js:
 * describing the story search picked set the wrong state half the time
 * (showcase stories ignore their controls, others bake in Loading or
 * Disabled), so the request is described on the component's first story
 * whose controls work. When it sets no control, the picked story is the
 * better answer ("a text field with a hint" → With hint), with the request's
 * text on it if its controls work.
 */
async function withState(r, query, { index, globals, frame }, signal) {
  const entry = index.entries[r.storyId];
  if (!entry?.importPath) return r;
  const described = await (await previewOf(frame.current)).describe(entry.importPath);
  const picked = described?.stories.find((s) => s.id === r.storyId);
  const base = described?.stories.find((s) => !s.ignoresArgs);
  if (!base) return r;
  const res = await fetch('/__edl/ask-args', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, title: described.title, story: base.name, argTypes: base.argTypes, args: base.initialArgs, globals }),
    signal,
  });
  const data = await res.json();
  if (!res.ok) return r;
  const labels = Object.fromEntries(Object.entries(data.labels ?? {}).filter(([k]) => k in data.args));
  const controls = Object.keys(data.args).filter((k) => !(k in labels));
  if (controls.length || Object.keys(data.globals).length) {
    return { ...r, storyId: base.id, storyName: base.name, args: data.args, texts: Object.keys(labels), globals: Object.keys(data.globals).length ? data.globals : undefined };
  }
  const text = Object.keys(labels).length && picked && !picked.ignoresArgs && Object.keys(labels).every((k) => typeof picked.initialArgs?.[k] === 'string');
  return text ? { ...r, args: labels, texts: Object.keys(labels) } : r;
}

/** What an answer set, for its row: "leadingIcon plus · “Add new client”". A cleared text (placeholder) is left out. */
function setText(r) {
  const texts = new Set(r.texts ?? []);
  const parts = [...Object.entries(r.args ?? {}), ...Object.entries(r.globals ?? {})]
    .filter(([, v]) => v !== '')
    .map(([k, v]) => (texts.has(k) ? `“${v}”` : typeof v === 'boolean' ? (v ? k : `no ${k}`) : `${k} ${v}`));
  // Text first: it says what the thing is.
  return parts.sort((a, b) => b.startsWith('“') - a.startsWith('“')).join(' · ');
}

/**
 * Answers by meaning, as an accordion: the highlighted answer opens to show
 * its story, rendered by the project's own preview (preview.html?embed).
 * Every answer's preview loads as soon as the answers arrive and stays
 * mounted; moving between answers only shows and hides them. (Mounting a
 * preview on open cost a full page load, 200-600 ms, on every move.)
 */
function MeaningGroup({ rows, status, none, error, active, setActive, globals, onSelect }) {
  const busy = status === 'waiting' || status === 'stale';
  const first = rows[0]?.index ?? -1;
  // The first answer opens when nothing by meaning is highlighted yet.
  const openIndex = rows.some((r) => r.index === active) ? active : first;
  return (
    <div role="group" aria-label="By meaning" aria-busy={busy} className={`shell-search-meaning${status === 'stale' ? ' is-stale' : ''}`}>
      <div className="shell-search-group shell-search-meaning__head">
        By meaning
        {busy && <span className="shell-search-meaning__status">Looking</span>}
      </div>
      {status === 'error' && <p className="shell-search-meaning__note">Search by meaning failed: {error}</p>}
      {none && status === 'done' && <p className="shell-search-meaning__note">{rows.length ? 'Nothing in the library does this. Closest:' : 'Nothing in the library does this.'}</p>}
      {rows.map((item) => {
        const expanded = item.index === openIndex;
        return (
          <div key={item.id} className={`shell-search-answer${expanded ? ' is-open' : ''}`}>
            <button
              type="button"
              id={`sr-m-${item.id}`}
              role="option"
              data-search-index={item.index}
              aria-selected={item.index === active}
              aria-expanded={expanded}
              className={`shell-search-item${item.index === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(item.index)}
              onClick={() => onSelect(item.id, item.state)}
            >
              <Sparkles size={16} strokeWidth={1.5} aria-hidden="true" />
              <span className="shell-search-item__label">{item.label}</span>
              <span className="shell-search-item__desc">
                {item.description}
                {item.set && <span className="shell-search-item__set"> · {item.set}</span>}
              </span>
            </button>
            {/* A picture of the story; a click on it opens the story too. */}
            <div className={`shell-search-answer__preview${expanded ? ' is-open' : ''}`} aria-hidden={!expanded} onClick={() => onSelect(item.id, item.state)}>
              <StoryThumb storyId={item.id} globals={{ ...globals, ...item.state?.globals }} args={item.state?.args} title={`${item.label} · ${item.description}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The story itself, from the project's preview, as a thumbnail. */
function StoryThumb({ storyId, globals, args, title }) {
  const [loaded, setLoaded] = useState(false);
  const g = Object.entries(globals ?? {}).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}:${v}`).join(';');
  const a = args && Object.keys(args).length ? `&args=${encodeURIComponent(JSON.stringify(args))}` : '';
  const src = `/preview.html?embed&id=${encodeURIComponent(storyId)}${g ? `&globals=${encodeURIComponent(g)}` : ''}${a}`;
  useEffect(() => setLoaded(false), [src]);
  return (
    <div className={`shell-search-thumb${loaded ? ' is-loaded' : ''}`}>
      <iframe src={src} title={`${title} preview`} tabIndex={-1} onLoad={() => setLoaded(true)} />
    </div>
  );
}
